import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Compass, ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Reveal";

const DHOW_IMG =
  "https://images.unsplash.com/photo-1633555442524-533869f78371?crop=entropy&cs=srgb&fm=jpg&w=2400&q=80";
const SPICE_IMG =
  "https://images.unsplash.com/photo-1601379760622-0d2e7ad24c11?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80";
const LANTERN_IMG =
  "https://images.unsplash.com/photo-1650217641889-4fee9a279d8a?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80";

const TRADE_PARTNERS = [
  {
    region: "China",
    years: "9th – 15th c.",
    note: "Ming-era junks carrying porcelain and silk anchored at Kozhikode. Admiral Zheng He visited the Zamorin’s court in 1407 and again in 1421 — Calicut was a fixed star on Asia’s map.",
  },
  {
    region: "Arabia & Persia",
    years: "7th – 17th c.",
    note: "Arab and Persian dhows turned Kozhikode into the gateway of the Indian Ocean. The Mappila community grew here, and Ibn Battuta called the port ‘one of the great harbours of the world.’",
  },
  {
    region: "Rome",
    years: "1st c. BCE – 3rd c. CE",
    note: "Roman amphorae and gold coins point to a Malabar-Rome pepper trade older than empires. Pliny grumbled in writing about Rome’s gold draining east for spices grown around here.",
  },
  {
    region: "Portugal",
    years: "1498 →",
    note: "Vasco da Gama landed at Kappad, 16 km north of the city, in May 1498 — opening the modern era of Indo-European exchange right on this coastline.",
  },
];

export default function Heritage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <main data-testid="heritage-page" className="bg-ocean text-parchment">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden grain">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={DHOW_IMG} alt="A dhow under sail in the Indian Ocean" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-ocean via-ocean/70 to-ocean" />
        </motion.div>

        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-28 md:pb-32">
          <Reveal>
            <div className="overline text-brass">Maritime Heritage · The Calicut Story</div>
          </Reveal>
          <Reveal delay={1}>
            <h1
              data-testid="heritage-headline"
              className="font-serif text-parchment text-7xl md:text-[12rem] leading-[0.85] tracking-tight mt-6"
            >
              The Zamorin
              <br />
              <em className="text-brass">Legacy.</em>
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-8 max-w-2xl text-parchment/80 text-lg leading-relaxed">
              For nearly a thousand years, Kozhikode was the world’s most important trading port east of
              Aden. Empires came here. Religions came here. Ideas came here. The spice route bent through
              this coast — and so does ours.
            </p>
          </Reveal>
        </div>
      </section>

      {/* OPENER */}
      <section className="py-20 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline text-brass">A short brag</div>
            <div className="divider-brass mt-6" />
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-4xl md:text-6xl">
              Calicut didn’t join globalisation.
              <br />
              <em className="text-brass">It invented it.</em>
            </h2>
            <p className="mt-8 text-lg text-parchment/75 leading-relaxed max-w-2xl">
              The Zamorins, rulers of Kozhikode, built a court so cosmopolitan that the city ran on a
              currency standard accepted from Aden to Malacca. Chinese, Arabs, Persians, Jews, Romans and
              Portuguese all called this port home — and all of it happened without conquest, just commerce
              and a quiet, ruthless reputation for honest weights.
            </p>
          </div>
        </div>
      </section>

      {/* TRADE PARTNERS — ASYMMETRIC */}
      <section className="py-12 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {TRADE_PARTNERS.map((p, i) => (
              <Reveal key={p.region} delay={i}>
                <li
                  data-testid={`trade-partner-${p.region.toLowerCase().split(' ')[0]}`}
                  className="grid grid-cols-12 gap-6 py-10 md:py-14 items-start group hover:bg-white/[0.03] transition-colors"
                >
                  <div className="col-span-12 md:col-span-1 font-serif text-3xl md:text-4xl text-brass">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <h3 className="font-serif text-4xl md:text-6xl leading-none">{p.region}</h3>
                    <div className="overline text-brass mt-4">{p.years}</div>
                  </div>
                  <div className="col-span-12 md:col-span-7 text-parchment/75 text-base md:text-lg leading-relaxed">
                    {p.note}
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* IMAGE TRIPTYCH */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-7">
            <figure className="relative aspect-[4/3] overflow-hidden">
              <img src={DHOW_IMG} alt="Beypore dhow under sail" className="w-full h-full object-cover" />
              <figcaption className="absolute bottom-4 left-4 overline text-parchment bg-ocean/70 px-3 py-2">
                The Beypore Dhow · still hand-built, 8 km from the city
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={1} className="md:col-span-5 flex flex-col gap-6">
            <figure className="relative aspect-[4/3] overflow-hidden">
              <img src={SPICE_IMG} alt="Raw Malabar spices" className="w-full h-full object-cover" />
              <figcaption className="absolute bottom-4 left-4 overline text-parchment bg-ocean/70 px-3 py-2">
                Black gold · the original Malabar export
              </figcaption>
            </figure>
            <figure className="relative aspect-[4/3] overflow-hidden">
              <img src={LANTERN_IMG} alt="Brass lantern" className="w-full h-full object-cover" />
              <figcaption className="absolute bottom-4 left-4 overline text-parchment bg-ocean/70 px-3 py-2">
                Brass · the metalwork of merchant houses
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* BEYPORE DHOW DEEP-DIVE */}
      <section className="py-20 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-brass">A craft, not a relic</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-4">
              The Beypore <em className="text-brass">Uru.</em>
            </h2>
          </div>
          <div className="md:col-span-7 text-parchment/75 text-lg leading-relaxed space-y-6">
            <p>
              Built entirely without blueprints, the Uru — a wooden dhow shaped in the boatyards of Beypore
              — has been crafted by Khalasi families for over 1,500 years. Today they’re commissioned by
              Gulf royalty as private yachts, still rising rib by rib on the same stretch of riverbank.
            </p>
            <p>
              That’s the Kozhikode way: ancient skill, contemporary clients, zero hype. We try to build
              this chapter the same way.
            </p>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-24 md:py-36 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <Compass size={36} weight="light" className="text-brass mb-6" />
            <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.9]">
              We inherit a port that <em className="text-brass">opened the world.</em>
              <br />
              The least we can do is open a few doors of our own.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/initiatives"
              data-testid="heritage-cta-initiatives"
              className="overline px-8 py-5 border border-brass text-brass hover:bg-brass hover:text-ocean transition-colors flex items-center gap-2"
            >
              See what we’re building <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
